import { MarketingLanguage, getMarketingLanguageFromPath } from "./marketingLocales";

// One entry per marketing page, generated into index.json by `npm run marketing:index`
// (scripts/import-framer-marketing-content.mjs). See README.md for the content model.
export type MarketingPageLanguage = MarketingLanguage;
export type MarketingPageTemplate = "article" | "landing" | "blogIndex" | "pricing";
export const MARKETING_PAGE_TEMPLATES: MarketingPageTemplate[] = ["article", "landing", "blogIndex", "pricing"];

export type MarketingContentIndexEntry = {
  route: string;
  lang: MarketingPageLanguage;
  section: string;
  slug: string;
  template: MarketingPageTemplate;
  title: string;
  description: string;
  heading: string | null;
  heroImage: string | null;
  /** Pixel size of heroImage, when the build could read it; the page reserves the image's box with it. */
  heroImageSize: { width: number; height: number } | null;
  author: string | null;
  publishedDate: string | null;
  category: string | null;
  alternateLanguageRoute: string | null;
  sortIndex: number | null;
  wordCount: number;
};

const indexes = new Map<MarketingPageLanguage, MarketingContentIndexEntry[]>();
const failures = new Map<MarketingLanguage, Error>();
const indexPromises = new Map<MarketingPageLanguage, Promise<void>>();
const indexLoaders = import.meta.glob<{ default: MarketingContentIndexEntry[] }>("./indexes/*.json");

export function loadMarketingContentIndex(language: MarketingPageLanguage): Promise<void> {
  if (indexes.has(language)) return Promise.resolve();
  let promise = indexPromises.get(language);
  if (!promise) {
    const loader = indexLoaders[`./indexes/${language}.json`];
    if (!loader) return Promise.reject(new Error(`Missing marketing index: ${language}`));
    promise = loader().then(module => { indexes.set(language, module.default); }).catch(error => {
      failures.set(language, error instanceof Error ? error : new Error(String(error)));
      throw error;
    });
    indexPromises.set(language, promise);
  }
  return promise;
}

function getMarketingContentIndex(language: MarketingPageLanguage): MarketingContentIndexEntry[] {
  const index = indexes.get(language);
  if (failures.has(language)) throw failures.get(language);
  if (!index) throw loadMarketingContentIndex(language);
  return index;
}

export type MarketingFrontMatterValue = string | number | boolean | string[];
export type MarketingFrontMatter = Record<string, MarketingFrontMatterValue>;

/**
 * Parses the small YAML subset used by the marketing Markdown files (the same subset the import
 * script writes): `key: "quoted string"`, `key: 123`, `key: true`, and lists of quoted strings
 * (`key:` followed by `  - "value"` lines). Any other line is ignored. A file without front matter
 * yields an empty front matter object and the whole text as the body.
 */
export function parseMarketingFrontMatter(fileText: string): { frontMatter: MarketingFrontMatter; body: string } {
  const normalizedText = fileText.replace(/\r\n/g, "\n");
  if (!normalizedText.startsWith("---\n")) return { frontMatter: {}, body: normalizedText };
  const frontMatterEndIndex = normalizedText.indexOf("\n---\n", 4);
  if (frontMatterEndIndex === -1) return { frontMatter: {}, body: normalizedText };

  const frontMatterLines = normalizedText.slice(4, frontMatterEndIndex).split("\n");
  const body = normalizedText.slice(frontMatterEndIndex + "\n---\n".length);
  const frontMatter: MarketingFrontMatter = {};
  // The key whose list items (`  - "value"`) are currently being collected, if any.
  let currentListKey: string | null = null;

  for (const line of frontMatterLines) {
    const listItemMatch = line.match(/^\s+-\s+(.*)$/);
    if (listItemMatch && currentListKey) {
      const listItemValue = parseFrontMatterScalar(listItemMatch[1]);
      (frontMatter[currentListKey] as string[]).push(String(listItemValue));
      continue;
    }
    const keyMatch = line.match(/^([A-Za-z_][A-Za-z0-9_]*):\s*(.*)$/);
    if (!keyMatch) continue;
    const [, key, rawValue] = keyMatch;
    const trimmedRawValue = rawValue.trim();
    if (trimmedRawValue === "" || trimmedRawValue === "[]") {
      frontMatter[key] = [];
      currentListKey = trimmedRawValue === "" ? key : null;
      continue;
    }
    currentListKey = null;
    frontMatter[key] = parseFrontMatterScalar(trimmedRawValue);
  }
  return { frontMatter, body };
}

function parseFrontMatterScalar(rawValue: string): string | number | boolean {
  const trimmedValue = rawValue.trim();
  if (trimmedValue.startsWith('"') && trimmedValue.endsWith('"') && trimmedValue.length >= 2) {
    try {
      return JSON.parse(trimmedValue);
    } catch {
      return trimmedValue.slice(1, -1);
    }
  }
  if (trimmedValue === "true") return true;
  if (trimmedValue === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmedValue)) return Number(trimmedValue);
  return trimmedValue;
}

/** Only the current language's metadata loads; other languages stay in separate chunks. */
export function findMarketingContentEntryByRoute(pathname: string): MarketingContentIndexEntry | null {
  const normalized = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
  return getMarketingContentIndex(getMarketingLanguageFromPath(pathname)).find(entry => entry.route.toLowerCase() === normalized) ?? null;
}

// Every page's Markdown as a lazily imported raw string, keyed by its path relative to this file.
// Each page becomes its own chunk, so only the visited page's text is downloaded.
const markdownModuleLoadersByPath = import.meta.glob<string>("./pages/**/*.md", { query: "?raw", import: "default" });

export async function loadMarketingPageMarkdown(entry: MarketingContentIndexEntry): Promise<string> {
  const markdownModulePath = `./pages/${entry.lang}/${entry.section}/${entry.slug}.md`;
  const loadMarkdownModule = markdownModuleLoadersByPath[markdownModulePath];
  if (!loadMarkdownModule) {
    throw new Error(`No Markdown file for marketing route ${entry.route} (expected ${markdownModulePath})`);
  }
  return loadMarkdownModule();
}

function isBlogPost(entry: MarketingContentIndexEntry): boolean {
  return entry.section === "blog" && entry.slug !== "index";
}

/** The language's blog posts, newest first (publishedDate desc, undated last; then sortIndex asc; then route). */
export function getBlogPostsForLanguage(lang: MarketingPageLanguage): MarketingContentIndexEntry[] {
  return getMarketingContentIndex(lang)
    .filter((entry) => entry.lang === lang && isBlogPost(entry))
    .sort((firstPost, secondPost) => {
      if (firstPost.publishedDate !== secondPost.publishedDate) {
        if (!firstPost.publishedDate) return 1;
        if (!secondPost.publishedDate) return -1;
        // ISO dates (YYYY-MM-DD) sort correctly as strings.
        return firstPost.publishedDate < secondPost.publishedDate ? 1 : -1;
      }
      const firstSortIndex = firstPost.sortIndex ?? Number.MAX_SAFE_INTEGER;
      const secondSortIndex = secondPost.sortIndex ?? Number.MAX_SAFE_INTEGER;
      if (firstSortIndex !== secondSortIndex) return firstSortIndex - secondSortIndex;
      return firstPost.route < secondPost.route ? -1 : firstPost.route > secondPost.route ? 1 : 0;
    });
}

export function isMarketingBlogPost(entry: MarketingContentIndexEntry): boolean {
  return isBlogPost(entry);
}

export { getMarketingPageHeading } from "./marketingPageHeading";
